import { FC, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Divider, Button, Box, Typography } from '@mui/material';

import { PostStatus, CampaignPPCOptions, CampaignPartnerOptions } from '@libs/types';
import { PostUpdateDto } from '@libs/dto';
import { useForm, useDrawer } from '@carcareers/contexts';
import {
  RooftopSelect,
  JobSelect,
  RadioInput,
  DateRangeInput,
  PaymentProfileSelect,
  TextInput,
  CheckboxListInput,
  Select,
  PromoSelect,
} from '@carcareers/components';
import { usePost } from '@carcareers/hooks';

interface PostUpdateFormProps {
  onSuccess?: () => void;
}

export const PostUpdateForm: FC<PostUpdateFormProps> = ({ onSuccess = () => undefined }) => {
  const navigate = useNavigate();
  const statusOptions = Object.values([PostStatus.PENDING, PostStatus.ACTIVE, PostStatus.CLOSED]);

  const { setOpen } = useDrawer();
  const { data, setErrors } = useForm<PostUpdateDto>();
  const { updateStartDate, updateEndDate } = usePost();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (data.startDate) {
      const response = await updateStartDate(data, { onSuccess });
      if (response?.data) {
        setOpen(false);
      } else if (response?.errors) {
        setErrors(response.errors);
        return;
      }
    }

    if (data.endDate) {
      const response = await updateEndDate(data, { onSuccess });
      if (response?.data) {
        setOpen(false);
      } else if (response?.errors) {
        setErrors(response.errors);
      }
    }
  };

  useEffect(() => {
    return () => {
      setOpen(false);
    };
  }, [setOpen]);

  return (
    <>
      <Typography variant="h6">Job Posting Details</Typography>
      <RooftopSelect name="job.rooftop" />
      <JobSelect useRooftop name="job" rooftopName="job.rooftop" />
      <Typography variant="h6">Status</Typography>
      <RadioInput options={statusOptions} name="status" />
      <Typography variant="h6">Campaign</Typography>
      <DateRangeInput nameStart="startDate" nameEnd="endDate" />
      <Select name="ppc" label="Price Per Click" options={CampaignPPCOptions} />
      <TextInput name="budget" label="Max Spend" type="number" />
      <CheckboxListInput name="partners" options={CampaignPartnerOptions} />
      <Typography variant="h6">Promo Code</Typography>
      <PromoSelect name="promo" />
      <Typography variant="h6">Payment Details</Typography>
      <TextInput name="totalPaymentDue" label="Total Payment Due" type="number" />
      <PaymentProfileSelect name="customerPaymentProfileId" />
      <Divider sx={{ flexGrow: 1 }} />
      <Box
        sx={(theme) => ({
          display: 'flex',
          gap: theme.spacing(2),
          justifyContent: 'flex-end',
          [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' },
          [theme.containerQueries('drawer').down(500)]: { flexDirection: 'column-reverse' },
        })}
      >
        <Button size="large" variant="outlined" color="secondary" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button size="large" variant="contained" color="primary" type="submit" onClick={handleSubmit}>
          Update Post
        </Button>
      </Box>
    </>
  );
};

export default PostUpdateForm; 